import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { UrlGenerator } from '@/utils/url-generator.util';
import { checkOrganizationExists } from '@/utils/tenant.util';
import OrganizationNotFound from '@/pages/public/OrganizationNotFound';

interface OrganizationValidatorProps {
  children: React.ReactNode;
}

const OrganizationValidator: React.FC<OrganizationValidatorProps> = ({ children }) => {
  const [isValidating, setIsValidating] = useState(true);
  const [isValidOrganization, setIsValidOrganization] = useState(true);
  const [_organizationSlug, setOrganizationSlug] = useState<string | null>(null);
  const location = useLocation();
  const previousSlugRef = useRef<string | null>(null);
  const currentHostnameRef = useRef<string>(window.location.hostname);

  useEffect(() => {
    const validateOrganization = async () => {
      // Only validate on login page and if not on main platform domain
      if (location.pathname === '/login' && !UrlGenerator.isMainPlatformDomain()) {
        const slug = UrlGenerator.getOrganizationSlugFromUrl();
        const currentHostname = window.location.hostname;

        // Check if domain or slug changed
        const domainChanged = currentHostname !== currentHostnameRef.current;
        const slugChanged = slug !== previousSlugRef.current;

        if (slug) {
          setOrganizationSlug(slug);
          
          const cacheKey = `org_validation_${slug}`;
          
          // Clear old cache if slug changed
          if (slugChanged && previousSlugRef.current) {
            const oldCacheKey = `org_validation_${previousSlugRef.current}`;
            sessionStorage.removeItem(oldCacheKey);
          }
          
          // Update refs
          previousSlugRef.current = slug;
          currentHostnameRef.current = currentHostname;

          // Only validate if slug/domain changed, otherwise use cache
          if (!slugChanged && !domainChanged) {
            // Slug and domain haven't changed, check cache
            const cachedResult = sessionStorage.getItem(cacheKey);

            if (cachedResult) {
              try {
                const cached = JSON.parse(cachedResult);
                // Check if cache is still valid (same slug and not expired)
                const cacheExpiry = cached.timestamp + (5 * 60 * 1000); // 5 minutes cache
                const now = Date.now();

                if (cached.slug === slug && now < cacheExpiry) {
                  // Use cached result - no API call needed
                  setIsValidOrganization(cached.valid);
                  setIsValidating(false);
                  return;
                } else {
                  // Cache expired, remove it
                  sessionStorage.removeItem(cacheKey);
                }
              } catch (error) {
                // Invalid cache, remove it
                sessionStorage.removeItem(cacheKey);
              }
            } else {
              // No cache but slug hasn't changed, assume valid (already validated)
              setIsValidOrganization(true);
              setIsValidating(false);
              return;
            }
          }

          // Slug/domain changed or cache expired - call API
          try {
            const result = await checkOrganizationExists(slug);

            if (result.success && result.exists && result.valid) {
              // Organization exists and is valid
              setIsValidOrganization(true);
              
              // Cache the result with timestamp
              sessionStorage.setItem(
                cacheKey,
                JSON.stringify({
                  slug: result.slug,
                  valid: result.valid,
                  timestamp: Date.now(),
                })
              );
              
              // Also store organization info for later use (legacy support)
              sessionStorage.setItem(
                'currentOrganization',
                JSON.stringify({
                  slug: result.slug,
                  valid: result.valid,
                })
              );
            } else {
              // Organization not found or invalid
              setIsValidOrganization(false);
              sessionStorage.removeItem('currentOrganization');
              
              // Cache invalid result too
              sessionStorage.setItem(
                cacheKey,
                JSON.stringify({
                  slug: slug,
                  valid: false,
                  timestamp: Date.now(),
                })
              );
            }
          } catch (error) {
            setIsValidOrganization(false);
            sessionStorage.removeItem('currentOrganization');
          }
        } else {
          // No slug found, treat as invalid
          setIsValidOrganization(false);
          sessionStorage.removeItem('currentOrganization');
        }
      } else {
        // Not on login page or on main platform domain, no validation needed
        setIsValidOrganization(true);
      }

      setIsValidating(false);
    };

    validateOrganization();
  }, [location.pathname]); // Only re-run when pathname changes (domain changes detected via ref comparison)

  // Show loading state while validating
  if (isValidating) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <div className="text-center">
            <div className="mx-auto h-12 w-12 text-blue-500 mb-4">
              <svg className="animate-spin h-full w-full" fill="none" viewBox="0 0 24 24">
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            </div>
            <p className="text-lg text-gray-600">Validating organization...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show not found page if organization is invalid
  if (!isValidOrganization) {
    return <OrganizationNotFound />;
  }

  // Render children if organization is valid or no validation needed
  return <>{children}</>;
};

export default OrganizationValidator;

